// ── S3: single source of truth for which roles may reach which route ────────
// This is the seed of the future per-user permissions map — keep it as data,
// never scatter role conditionals across route/page components.
//
// S4 — Rule: the owner (a Firebase Auth account with the EXPLICIT custom
// claim role === 'owner') always passes, on every route, and is never listed
// below. A missing/null role claim is no longer treated as owner — it passes
// nothing (see isRouteAllowed below; this was the app-wide route-guard
// bypass that let any account which failed to receive a claim reach every
// owner-only route). Every other role must be explicitly named for a route
// or it is denied — deny-by-default, not allow-by-default. A route with no
// entry at all is owner-only (matches the PRIVATE/AppLayout segment's
// intent).
//
// Dynamic segments (":id", ":jobId", etc.) are matched with react-router's
// own matchPath, so a table key is a route PATTERN, not a literal path.
import { matchPath } from 'react-router-dom';

export const ROUTE_ROLES = {
  // ── SALES segment (src/pages/sales/*) ──────────────────────────────────
  '/canvass-home':            ['sales_rep', 'sales_manager'],
  '/rep/jobs':                 ['sales_rep', 'sales_manager'],
  '/rep/jobs/:jobId':          ['sales_rep', 'sales_manager'],
  '/rep/guide':                ['sales_rep', 'sales_manager'],
  '/rep/messages':             ['sales_rep', 'sales_manager'],
  '/rep/messages/:threadId':   ['sales_rep', 'sales_manager'],
  '/manager':                  ['sales_manager'],
  '/manager/guide':            ['sales_manager'],
  // The owner's /team is an AppLayout route; a manager reaches the same page
  // in their own shell at /manager/team. Both are listed so the guard allows
  // whichever shell the viewer belongs to.
  '/team':                      ['sales_manager'],
  '/manager/team':              ['sales_manager'],

  // ── CREW segment (src/pages/crew/*) ────────────────────────────────────
  '/crew':                      ['crew'],
  '/crew/jobs/:jobId':          ['crew'],
  '/crew/instructions':         ['crew'],
  '/crew/messages':             ['crew'],
  '/crew/messages/:threadId':   ['crew'],

  // ── RESEARCH BOT segment — one route, nothing else. research_bot must
  // appear in EXACTLY this entry and nowhere else in this table; it has no
  // grant in GRANTABLE_ROUTES either. Its only server-side access is
  // getNextResearchBatch/writeResearchResult (functions/index.js) plus the
  // matching leads/{leadId} get+8-field-update block (firestore.rules) —
  // this route entry is what lets it actually REACH the /research screen
  // that calls those two endpoints; every other route still denies it by
  // the same not-listed-for-this-role default every other role gets. owner
  // is included so the owner can open /research too, for testing.
  '/research':                  ['research_bot', 'owner'],

  // ── PRIVATE (owner) segment — every route below is owner-only today.
  // sales_rep's ruling ("own leads/jobs surfaces") maps to the SALES-segment
  // /rep/jobs* routes above, not these owner-namespaced equivalents (Leads.js
  // and JobDetail.js render ALL leads/jobs, not just a rep's own — granting
  // access here would leak beyond the ruling). No entry below grants any
  // non-owner role access; each is listed explicitly (empty array) so the
  // table stays the complete, auditable picture rather than relying on the
  // "no entry = deny" fallback for routes we KNOW should be owner-only.
  // Command Center task: opened to sales_manager/sales_rep so each has
  // somewhere to see their own assigned tasks/alerts (ask #4) — a real,
  // deliberate reversal of this route's prior owner-only status (see the
  // segment note above and GRANTABLE_ROUTES below, which still correctly
  // can't grant it via a toggle; this is a role-default change, not a
  // toggle). Their own view is hard-restricted server-side to their own
  // items (Dashboard.js's role check + the tasks collectionGroup rule in
  // firestore.rules), not just a client-side filter default — crew is
  // deliberately left out of this (no Tasks tab, no Command Center access,
  // matches their existing scope elsewhere in this table).
  // RoofBeam OS — owner + sales_manager (the owner's ruling). The Permit
  // Desk's output is office work, so the office can see it and run it. Its
  // own entitlement check runs INSIDE the page too, so a company without the
  // OS on its plan gets the upgrade panel rather than the shell, and no agent
  // can be run from a route grant alone.
  '/os':                              ['sales_manager'],
  // Field Mode's home and its day/week view. Same reach as /dashboard — a rep
  // in a driveway needs today's list as much as the owner does.
  '/today':                           ['sales_manager', 'sales_rep'],
  '/schedule':                        ['sales_manager', 'sales_rep'],
  '/dashboard':                       ['sales_manager', 'sales_rep'],
  '/leads':                           [],
  '/website-leads':                   [],
  '/canvassing':                      [],
  '/prospects':                       [],
  // Proposals — unlike Leads/JobDetail (which browse EVERYTHING and are kept
  // owner-only for that reason, see the segment note above), this page's own
  // data is already rep-scoped server-side (getProposalsPipeline in
  // functions/index.js: a sales_rep only ever gets rows where repId is their
  // own uid; sales_manager sees the whole company pipeline, a deliberate
  // product choice for this one page). The route itself is safe to open to
  // both roles.
  '/proposals':                       ['sales_rep', 'sales_manager'],
  '/jobs':                            [],
  '/jobs/:id':                        [],
  // BUG FIX (follow-up #2): App.js registers a SEPARATE route pattern for a
  // job's own tab URLs (App.js: <Route path="/jobs/:id/:tabSlug">, so
  // /jobs/:id/tasks carries one more path segment than /jobs/:id) — this
  // table had no entry for it at all. isRouteAllowed's matchPath({end:true})
  // only matches a pattern with the SAME segment count, so an unlisted
  // pattern denies by default (see this table's own stated design): every
  // job tab URL was unreachable by any non-owner role, independent of the
  // separate PrivateRoute.js claims-resolution bug this same follow-up
  // fixes. Matches '/jobs/:id' immediately above.
  '/jobs/:id/:tabSlug':               [],
  '/storm-tracker':                   [],
  '/neighborhoods':                   [],
  '/map':                             [],
  '/contacts':                        [],
  '/contacts/:id':                    [],
  '/properties':                      [],
  '/properties/:id':                  [],
  '/takeoff':                         [],
  // Owner-only, explicitly. It would be owner-only anyway (an absent entry
  // denies every non-owner role), but a page that lists which jobs are blocked
  // from ordering should say so in the table rather than rely on the default.
  '/metal-jobs':                      [],
  '/estimator':                       [],
  '/catalog':                         [],
  '/documents':                       [],
  // A single document, scoped by its own URL params — not a browse-everything
  // page like Leads/JobDetail. firestore.rules already restricts a rep's read
  // of jobs/{jobId}/documents/{docId} to jobs they created, so granting the
  // ROUTE here can't surface anything the data layer wouldn't already deny.
  // Needed so a rep/manager can open a proposal card from the new /proposals
  // pipeline page.
  '/documents/view/:jobId/:docId':    ['sales_rep', 'sales_manager'],
  '/calendar':                        [],
  '/todos':                           [],
  '/messages':                        [],
  '/messages/:threadId':              [],
  '/profit':                          [],
  '/contractors':                     [],
  '/commercial':                      [],
  '/ai-agents':                       [],
  '/settings':                        [],
  '/solar-batch':                     [],
  '/approvals':                       [],
  // Owner-only for now, per the UX/nav audit — a thin read-only wrapper
  // around jobs(jobType=='insurance') + supplementPackages, no new data.
  '/claims':                          [],
  // ⚠️ THE PERMIT DESK IS OFFICE WORK. The owner's ruling on RoofBeam OS part
  // 3 gave sales_manager read/write on the permit and NOC records, and the
  // rules already do — but the route was owner-only, so a manager could not
  // reach the desk those grants exist for. The failing read was a LIST of
  // jobs, fixed in firestore.rules; this is the other half.
  '/permits':                         ['sales_manager'],
};

// ── S3 amended: per-user permission toggles (grant-only) ────────────────────
// A managed user's userProfiles/{uid}.permissions map can ADD access to the
// routes below, on top of whatever their role default already grants. A
// toggle can never SUBTRACT access and never appears here for a route that
// isn't listed — so the NOT-grantable set (financials/profit, dashboard,
// settings, catalog, commercial, contractors) can never be reached this way;
// simply not being a key in this object is what keeps them out.
export const GRANTABLE_ROUTES = {
  '/storm-tracker': 'stormTrackerView',   // view-only — admin buttons stay role-gated (see StormTracker.js canGenerateLeads and the server-side S2b gates, unchanged)
  '/estimator':      'estimatorAccess',
  '/canvassing':      'canvassingAccess',
};

// role: the caller's custom claim ('owner' | 'sales_rep' | 'sales_manager' |
// 'crew' | 'guest' | ...) or null/undefined for a token with NO claim at all.
// S4v2 PHASE C FOLLOW-UP: the Phase A dual-accept null branch has been
// removed — matches firestore.rules' own Phase C cutover (the root catch-all
// no longer grants role==null anything either). A missing/null role claim
// is no longer owner-equivalent here; only the explicit 'owner' claim is.
// permissions: the caller's userProfiles/{uid}.permissions map, or {} if they
// have none yet / aren't a role that gets one. Optional — omitting it just
// means no toggle grants apply, so every existing call site keeps working.
export function isRouteAllowed(pathname, role, permissions = {}) {
  if (role === 'owner') return true; // owner — always allowed, on every route
  for (const [pattern, roles] of Object.entries(ROUTE_ROLES)) {
    if (matchPath({ path: pattern, end: true }, pathname)) {
      if (roles.includes(role)) return true;
      const grantKey = GRANTABLE_ROUTES[pattern];
      return !!grantKey && permissions[grantKey] === true;
    }
  }
  // Unlisted route (shouldn't happen — every PrivateRoute-guarded path above
  // has an entry) — deny by default rather than silently allow.
  return false;
}
